import { IonSpinner, useIonToast } from '@ionic/react';
import { FunctionComponent, useEffect, useState } from 'react';
import Icon from './icons/Icon';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';
import Trans from 'util/Trans';
import { useTranslation } from 'react-i18next';
import React from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

export interface MapMarker {
  id: string;
  lat: number;
  long: number;
  title?: string;
  iconName?: string;
  color?: string;
  width?: number;
  onClick?: () => void;
  markerType?: 'default' | 'publicLocation'; // NEU: Marker-Typ für spezielle Darstellung
}

export interface MapComponentProps {
  lat?: number;
  long?: number;
  markers?: MapMarker[];
  showCurrentLocation?: boolean;
  defaultZoom?: number;
  height?: number;
}

const defaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  shadowSize: [41, 41],
});

// Helper to get the primary color from CSS variables
function getPrimaryColor() {
  const computedStyle = getComputedStyle(document.documentElement);
  const primaryColor = computedStyle
    .getPropertyValue('--ion-color-primary')
    .trim();
  return primaryColor || '#3880ff';
}

// Create a custom Leaflet icon with primary color
function createPrimaryIcon(primaryColor: string) {
  // SVG Marker (Leaflet default shape, but fill color is dynamic)
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 11.046 16 32 16 32s16-20.954 16-32C32 7.163 24.837 0 16 0z" fill="${primaryColor}"/>
      <circle cx="16" cy="16" r="7" fill="#fff"/>
    </svg>
  `);
  return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`,
    iconSize: [32, 48],
    iconAnchor: [16, 48],
    popupAnchor: [0, -48],
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    shadowSize: [41, 41],
  });
}

// Helper to get the secondary color from CSS variables
function getSecondaryColor() {
  const computedStyle = getComputedStyle(document.documentElement);
  const secondaryColor = computedStyle
    .getPropertyValue('--ion-color-secondary')
    .trim();
  return secondaryColor || '#3dc2ff';
}

// Create a custom Leaflet icon with secondary color
function createSecondaryIcon(secondaryColor: string) {
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 11.046 16 32 16 32s16-20.954 16-32C32 7.163 24.837 0 16 0z" fill="${secondaryColor}"/>
      <circle cx="16" cy="16" r="7" fill="#fff"/>
    </svg>
  `);
  return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`,
    iconSize: [32, 48],
    iconAnchor: [16, 48],
    popupAnchor: [0, -48],
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    shadowSize: [41, 41],
  });
}

// Punkt-Icon für Runs (z.B. ein gefüllter Kreis in Secondary-Farbe)
function createDotIcon(color: string) {
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
      <circle cx="12" cy="12" r="8" fill="${color}" />
      <circle cx="12" cy="12" r="11" fill="white" fill-opacity="0.2"/>
    </svg>
  `);
  return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
}

// Dunkleres Punkt-Icon für Public Locations
function createDarkDotIcon() {
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
      <circle cx="12" cy="12" r="8" fill="#222" />
      <circle cx="12" cy="12" r="11" fill="white" fill-opacity="0.15"/>
    </svg>
  `);
  return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
}

const MapComponent: FunctionComponent<MapComponentProps> = ({
  lat,
  long,
  markers = [],
  showCurrentLocation = true,
  defaultZoom = 13,
  height = 1000,
}) => {
  const [present] = useIonToast();
  const [ionSecondaryColor, setIonSecondaryColor] = useState('#3dc2ff'); // Ionic default secondary
  const [hasShownLocationError, setHasShownLocationError] = useState(false);
  const [primaryIcon, setPrimaryIcon] = useState<L.Icon>();
  const [secondaryIcon, setSecondaryIcon] = useState<L.Icon>();
  const [dotIcon, setDotIcon] = useState<L.Icon>();
  const [darkDotIcon, setDarkDotIcon] = useState<L.Icon>();
  const { t } = useTranslation();

  // Definiere die Trans-Nachricht als String für Toasts (TypeScript-sicher)
  let messageLocationNotAvailable = '';
  const transElement = Trans({
    i18nKey: 'LOCATION_NOT_AVAILABLE',
    defaultValue:
      'Standort nicht verfügbar. Bitte prüfen Sie Ihre Location-Berechtigung in den App-Einstellungen.',
  });
  const children =
    (transElement &&
      typeof transElement === 'object' &&
      (transElement as any).props?.children) ||
    undefined;
  if (typeof children === 'string') {
    messageLocationNotAvailable = children;
  } else if (Array.isArray(children)) {
    messageLocationNotAvailable = children.join('');
  } else if (children) {
    messageLocationNotAvailable = String(children);
  } else {
    messageLocationNotAvailable =
      'Standort nicht verfügbar. Bitte prüfen Sie Ihre Location-Berechtigung in den App-Einstellungen.';
  }

  // Get the actual Ionic secondary color from CSS variables
  useEffect(() => {
    const getIonicSecondaryColor = () => {
      const computedStyle = getComputedStyle(document.documentElement);
      const secondaryColor = computedStyle
        .getPropertyValue('--ion-color-secondary')
        .trim();

      if (secondaryColor) {
        // If it's a hex color, use it directly
        if (secondaryColor.startsWith('#')) {
          return secondaryColor;
        }
        // If it's RGB values, convert to hex or use rgb()
        if (secondaryColor.includes(',')) {
          return `rgb(${secondaryColor})`;
        }
        // If it's just the RGB values without rgb(), add rgb()
        return `rgb(${secondaryColor})`;
      }
      return '#3dc2ff';
    };
    const color = getIonicSecondaryColor();
    setIonSecondaryColor(color);
  }, []);

  useEffect(() => {
    setPrimaryIcon(createPrimaryIcon(getPrimaryColor()));
  }, []);

  useEffect(() => {
    setSecondaryIcon(createSecondaryIcon(getSecondaryColor()));
  }, []);

  useEffect(() => {
    setDotIcon(createDotIcon(getSecondaryColor()));
  }, []);

  useEffect(() => {
    setDarkDotIcon(createDarkDotIcon());
  }, []);

  useEffect(() => {
    if (showCurrentLocation && (!lat || !long) && !hasShownLocationError) {
      const errorTimer = setTimeout(() => {
        if (!lat || !long) {
          setHasShownLocationError(true);
          present({
            message: messageLocationNotAvailable,
            duration: MESSAGE_ALIVE_MS,
            color: 'warning',
            position: MESSAGE_POSITION,
          });
        }
      }, 10000); // Show error after 5 seconds

      return () => clearTimeout(errorTimer);
    }
    return undefined;
  }, [lat, long, showCurrentLocation, hasShownLocationError, present]);

  if (showCurrentLocation && (!lat || !long)) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <IonSpinner
          name="crescent"
          style={{
            width: 48,
            height: 48,
            '--color': ionSecondaryColor,
          }}
        />
      </div>
    );
  }

  const showMap = () => {
    const center: [number, number] = lat && long ? [lat, long] : [0, 0];
    const zoom = lat && long ? defaultZoom : 2;
    if (!lat || !long) {
      setTimeout(() => {
        present({
          message: messageLocationNotAvailable,
          duration: MESSAGE_ALIVE_MS,
          color: 'warning',
          position: MESSAGE_POSITION,
        });
      }, 10000);
    }

    return (
      <div style={{ width: '100%', height: '100%' }}>
        <MapContainer
          center={center}
          zoom={zoom}
          zoomSnap={0.05}
          style={{ width: '100%', height: height }}
          scrollWheelZoom={true}
          zoomControl={false} // Standard-ZoomControl deaktivieren
        >
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* Aktueller Standort */}
          {showCurrentLocation && lat && long && secondaryIcon && (
            <Marker position={[lat, long]} icon={secondaryIcon}>
              <Popup>Aktueller Standort</Popup>
            </Marker>
          )}
          {/* Custom Marker */}
          {dotIcon &&
            darkDotIcon &&
            markers.map((marker) => {
              // Marker-Type entscheidet über Icon und Interaktivität
              if (marker.markerType === 'publicLocation') {
                return (
                  <Marker
                    key={marker.id}
                    position={[marker.lat, marker.long]}
                    icon={L.divIcon({
                      className: '',
                      html: `<div style="width:18px;height:18px;border-radius:50%;background:${marker.color || '#3dc2ff80'};opacity:1;"></div>`,
                    })}
                    eventHandlers={{
                      click: () =>
                        present({
                          message: t(
                            'RUN_NOT_DOWNLOADABLE',
                            'Dieser Run ist noch nicht als Download verfügbar.'
                          ),
                          duration: MESSAGE_ALIVE_MS,
                          color: 'warning',
                          position: MESSAGE_POSITION,
                        }),
                    }}
                  />
                );
              }
              // Standard-Marker (interaktiv)
              return (
                <Marker
                  key={marker.id}
                  position={[marker.lat, marker.long]}
                  icon={dotIcon}
                  eventHandlers={{
                    click: marker.onClick,
                  }}
                >
                  {marker.title && <Popup>{marker.title}</Popup>}
                </Marker>
              );
            })}
        </MapContainer>
      </div>
    );
  };

  return showMap();
};

export default MapComponent;
